import { HttpError } from '@/core/api/http-client'
import type { Paged } from '@/domain/entities/paging'
import type { Printer, PrinterInput, PrinterListParams } from '@/domain/entities/printer.entity'
import type { PrinterRepository } from '@/domain/repositories/printer.repository'

const KEY = 'pos-dashboard:printers:'

const INITIAL_PRINTERS: Printer[] = [
  {
    id: 'printer-1',
    outletId: 'default',
    name: 'Printer Kasir Utama',
    printerType: 'ip_ethernet',
    address: '192.168.1.200:9100',
    paperSize: '80mm',
    isCashier: true,
    isChecker: true,
    isKitchen: false,
    isMultipleCashier: true,
    status: true,
    categoryIds: [],
    categories: [],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'printer-2',
    outletId: 'default',
    name: 'Printer Dapur & Bar',
    printerType: 'ip_ethernet',
    address: '192.168.1.201:9100',
    paperSize: '80mm',
    isCashier: false,
    isChecker: true,
    isKitchen: true,
    isMultipleCashier: false,
    status: true,
    categoryIds: [],
    categories: [],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
]

export class PrinterMockRepository implements PrinterRepository {
  private getStorageKey(outletId: string): string {
    return `${KEY}${outletId}`
  }

  private read(outletId: string): Printer[] {
    try {
      const raw = localStorage.getItem(this.getStorageKey(outletId))
      if (raw !== null) {
        const parsed = JSON.parse(raw) as Printer[]
        return parsed.map((p) => ({
          ...p,
          status: typeof p.status === 'boolean' ? p.status : true,
        }))
      }
    } catch {
      /* ignore storage errors */
    }

    // Hanya outlet 'default' yang mendapat contoh printer awal.
    // Outlet lainnya selalu mulai dari kosong ([]) agar tidak bercampur antar-outlet.
    if (outletId === 'default') {
      const initial = INITIAL_PRINTERS.map((p) => ({ ...p, outletId }))
      this.write(outletId, initial)
      return initial
    }

    return []
  }

  private write(outletId: string, items: Printer[]): void {
    localStorage.setItem(this.getStorageKey(outletId), JSON.stringify(items))
  }

  async list(outletId: string, params?: PrinterListParams): Promise<Paged<Printer>> {
    let items = this.read(outletId)

    if (params?.search) {
      const q = params.search.toLowerCase()
      items = items.filter(
        (p) => p.name.toLowerCase().includes(q) || p.address.toLowerCase().includes(q),
      )
    }

    const page = params?.page ?? 1
    const limit = params?.limit ?? 20
    const total = items.length
    const pagedItems = items.slice((page - 1) * limit, page * limit)
    const hasNext = page * limit < total

    return {
      items: pagedItems,
      total,
      page,
      limit,
      hasNext,
    }
  }

  async getById(outletId: string, id: string): Promise<Printer> {
    const items = this.read(outletId)
    const item = items.find((p) => p.id === id)
    if (!item) {
      throw new HttpError(404, 'Not Found', '/printers', 'Printer tidak ditemukan.')
    }
    return item
  }

  async create(outletId: string, input: PrinterInput): Promise<Printer> {
    const items = this.read(outletId)
    const newId = `printer-${Date.now()}`
    const now = new Date().toISOString()

    const newPrinter: Printer = {
      id: newId,
      outletId,
      name: input.name,
      printerType: input.printerType,
      address: input.address,
      paperSize: input.paperSize,
      isCashier: input.isCashier,
      isChecker: input.isChecker,
      isKitchen: input.isKitchen,
      isMultipleCashier: input.isMultipleCashier,
      status: true,
      categoryIds: input.categoryIds ?? [],
      categories: (input.categoryIds ?? []).map((id) => ({ id, name: `Kategori ${id}` })),
      createdAt: now,
      updatedAt: now,
    }

    items.unshift(newPrinter)
    this.write(outletId, items)
    return newPrinter
  }

  async update(outletId: string, id: string, input: PrinterInput): Promise<Printer> {
    const items = this.read(outletId)
    const idx = items.findIndex((p) => p.id === id)
    if (idx === -1) {
      throw new HttpError(404, 'Not Found', '/printers', 'Printer tidak ditemukan.')
    }

    const updated: Printer = {
      ...items[idx],
      name: input.name,
      printerType: input.printerType,
      address: input.address,
      paperSize: input.paperSize,
      isCashier: input.isCashier,
      isChecker: input.isChecker,
      isKitchen: input.isKitchen,
      isMultipleCashier: input.isMultipleCashier,
      categoryIds: input.categoryIds ?? [],
      categories: (input.categoryIds ?? []).map((catId) => ({
        id: catId,
        name: `Kategori ${catId}`,
      })),
      updatedAt: new Date().toISOString(),
    }

    items[idx] = updated
    this.write(outletId, items)
    return updated
  }

  async delete(outletId: string, id: string): Promise<void> {
    const items = this.read(outletId).filter((p) => p.id !== id)
    this.write(outletId, items)
  }

  async toggleStatus(outletId: string, id: string, status: boolean): Promise<Printer> {
    const items = this.read(outletId)
    const idx = items.findIndex((p) => p.id === id)
    if (idx === -1) {
      throw new HttpError(404, 'Not Found', '/printers', 'Printer tidak ditemukan.')
    }

    items[idx] = {
      ...items[idx],
      status,
      updatedAt: new Date().toISOString(),
    }
    this.write(outletId, items)
    return items[idx]
  }
}
